import type { Chart } from "../Chart";
import { Theme, Fonts } from "../theme";

const ATR_COLOR = "#2962ff";
const TR_COLOR = "#f0b90b";
const PAD = 10;

// Permanent bottom pane: ATR (RMA-14, from source data) + TR, both in points.
export function drawIndicators(ctx: CanvasRenderingContext2D, chart: Chart) {
  const data = chart.data;
  if (!data) return;
  const vp = chart.viewport;
  const top = vp.plot.height;
  const h = chart.indicatorHeight;
  const w = vp.plot.width;
  const bottom = top + h;

  // background + separators
  ctx.save();
  ctx.fillStyle = Theme.paneBg;
  ctx.fillRect(0, top, chart.cssWidth, h);
  ctx.strokeStyle = Theme.axisBorder;
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(0, top + 0.5);
  ctx.lineTo(chart.cssWidth, top + 0.5);
  ctx.stroke();

  const [lo, hi] = vp.visibleRange();
  let min = Infinity,
    max = -Infinity;
  for (let i = lo; i < hi; i++) {
    const a = data.atrAt(i);
    const t = data.trAt(i);
    if (isFinite(a)) {
      if (a < min) min = a;
      if (a > max) max = a;
    }
    if (isFinite(t)) {
      if (t < min) min = t;
      if (t > max) max = t;
    }
  }
  if (!isFinite(min) || !isFinite(max)) {
    ctx.restore();
    return;
  }
  if (min === max) {
    min -= 1;
    max += 1;
  }
  min = Math.max(0, min - (max - min) * 0.08);
  const range = max - min || 1;
  const yOf = (v: number) => bottom - PAD - ((v - min) / range) * (h - PAD * 2);

  // clip to pane plot width
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, top, w, h);
  ctx.clip();

  drawSeries(ctx, chart, lo, hi, (i) => data.trAt(i), yOf, TR_COLOR, 1);
  drawSeries(ctx, chart, lo, hi, (i) => data.atrAt(i), yOf, ATR_COLOR, 1.6);
  ctx.restore();

  // min/max scale labels (inside pane, right-aligned so the price gutter can't cover them)
  ctx.font = Fonts.axis;
  ctx.fillStyle = Theme.textDim;
  ctx.textBaseline = "middle";
  ctx.textAlign = "right";
  ctx.fillText(max.toFixed(2), w - 6, top + PAD);
  ctx.fillText(min.toFixed(2), w - 6, bottom - PAD);

  // legend (value at hovered or last bar)
  let idx = chart.hoverIndex;
  if (idx < 0) {
    idx = hi - 1;
    while (idx >= lo && !isFinite(data.atrAt(idx)) && !isFinite(data.trAt(idx))) idx--;
  }
  const atrV = idx >= 0 ? data.atrAt(idx) : NaN;
  const trV = idx >= 0 ? data.trAt(idx) : NaN;
  ctx.font = Fonts.legend;
  ctx.textBaseline = "top";
  ctx.textAlign = "left";
  let x = 10;
  const y = top + 6;
  ctx.fillStyle = Theme.textDim;
  ctx.fillText("ATR", x, y);
  x += ctx.measureText("ATR ").width + 2;
  ctx.fillStyle = ATR_COLOR;
  const atrStr = isFinite(atrV) ? atrV.toFixed(2) : "—";
  ctx.fillText(atrStr, x, y);
  x += ctx.measureText(atrStr).width + 14;
  ctx.fillStyle = Theme.textDim;
  ctx.fillText("TR", x, y);
  x += ctx.measureText("TR ").width + 2;
  ctx.fillStyle = TR_COLOR;
  ctx.fillText(isFinite(trV) ? trV.toFixed(2) : "—", x, y);

  ctx.restore();
}

function drawSeries(
  ctx: CanvasRenderingContext2D,
  chart: Chart,
  lo: number,
  hi: number,
  get: (i: number) => number,
  yOf: (v: number) => number,
  color: string,
  width: number
) {
  const vp = chart.viewport;
  ctx.strokeStyle = color;
  ctx.lineWidth = width;
  ctx.beginPath();
  let started = false;
  for (let i = lo; i < hi; i++) {
    const v = get(i);
    if (!isFinite(v)) {
      started = false;
      continue;
    }
    const x = vp.xCenterOfIndex(i);
    const y = yOf(v);
    if (!started) {
      ctx.moveTo(x, y);
      started = true;
    } else {
      ctx.lineTo(x, y);
    }
  }
  ctx.stroke();
}
